import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Forms } from "..";
import { Loading } from "../../../common/components/Loading";
import { DataRequestDetail } from "../../../common/models";
import { NetworkRequestState } from "../../../common/models/NetworkRequest";
import { Error as RequestsError } from "../../copy/requests";
import { useRegulationRequestTypes } from "../../hooks/useRegulationRequestTypes";
import { Error } from "../Error";

const localeSorter =
  (reverse = false) =>
  (a, b) => {
    return reverse ? b.localeCompare(a) : a.localeCompare(b);
  };

type EditDataRequestDialogProps = {
  open: boolean;
  request: DataRequestDetail;
  onClose: () => void;
  onDone: () => void;
  updateRequestState: NetworkRequestState;
  handleSubmit: (values: any) => void;
};

export const EditDataRequestDialog = ({
  open,
  request,
  onClose,
  onDone,
  updateRequestState,
  handleSubmit,
}: EditDataRequestDialogProps) => {
  const [everRendered, setEverRendered] = useState(false);

  const { regulationRequestTypes, loading, error } = useRegulationRequestTypes();

  // auto-close dialog on success
  useEffect(() => {
    if (updateRequestState.success) {
      onDone();
    }
  }, [updateRequestState.success, onDone]);

  // complete our page load
  useEffect(() => {
    if (!loading) {
      setEverRendered(true);
    }
  }, [loading]);

  if (!everRendered) {
    return null;
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Error message={error} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const drtOptions = (regulationRequestTypes || [])
    .slice()
    .filter((drt) => drt.workflowType === request.dataRequestType.workflowType)
    .map((drt) => ({
      label: `${drt.regulationLaw.abbreviation} - ${drt.label}`,
      value: drt.id,
    }))
    .sort((a, b) => localeSorter()(a.label, b.label));

  return (
    <Dialog
      fullWidth={true}
      maxWidth={"sm"}
      open={open}
      onClose={onClose}
      aria-labelledby="update-request-dialog-title"
    >
      <DialogTitle id="update-request-dialog-title">Edit Request</DialogTitle>

      {updateRequestState.running && (
        <DialogContent>
          <Loading />
        </DialogContent>
      )}

      {updateRequestState.error && (
        <>
          <DialogContent>
            <Error message={updateRequestState.errorMessage || RequestsError.UpdateRequest} />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      )}

      {!updateRequestState.started && (
        <Forms.EditDataRequest
          onSubmit={handleSubmit}
          onClose={onClose}
          request={request}
          dataRequestTypeOptions={drtOptions}
        />
      )}
    </Dialog>
  );
};
